import React, { useEffect, useState } from 'react';
import { countries } from 'data';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { getMomentDate, isValidDate } from '../../../../utilities/validation';
import {
  TextareaWithConfirm,
  DateInputWithConfirm,
  AutocompleteInputWithConfirm,
  getStringFromDate,
  getDateFromString,
  RadioOptionsWithConfirm,
  CardContainer,
  CardWithModal
} from 'shared-components';
import QuestionPage from '../../../templates/question';
import OtherNationality from './otherNationality';
import { Q } from '../../../../store/student/types';
import { ConfigField, IOtherNationality } from '../../../../types';
import { AppliedEUSSStatus, EUSSStatus } from 'types';
import { OtherGenderErrorMsg } from './utils';
import { titleAppend } from '../../../../utilities/head';
import { useDispatch, useSelector } from 'react-redux';
import {
  createNavigateForwardAction,
  createNavigateBackwardAction,
  createNavigateToAction
} from '../../../../middleware/actions';
import * as StudentActions from '../../../../store/student/actions';
import * as PIActions from '../../../../store/student/personalInfo/actions';
import { Pages } from '../../../../store/student/personalInfo/types';
import { toggleTaskMenu } from '../../../../store/ui/actions';
import { ApplicationState } from '../../../../store';
import { getPageStatus } from './utils';
import { Visible } from './utils';

const countryNames = countries.map(country => country.countryName);
const nationalitiesNames = countries.map(nationality => nationality.nationality);

const currentStep = 2;
const steps = 10;

const PersonalQPage2 = () => {
  const dispatch = useDispatch();

  const {
    Gender: genderConfig,
    DOB: dobConfig,
    BirthCountry: birthCountryConfig,
    Nationality: nationalityConfig,
    AppliedForEUSS: appliedForEUSSConfig,
    GrantedEUSSStatus: grantedEUSSStatusConfig,
    HasOtherNationality: hasOtherNationalityConfig,
    IsCountryOfNationality: isCountryOfNationalityConfig,
    CountryOfVisaApplication: countryOfVisaApplicationConfig,
    LivedInUKLast12Months: livedInUKLast12MonthsConfig,
    ReasonWhyNotCountryOfNationality: reasonWhyNotCountryOfNationalityConfig
  }: {
    [key: string]: ConfigField;
  } = useSelector((state: ApplicationState) => state.config.Checklist.PersonalInfo.Page2);

  const {
    gender: genderFromState = genderConfig?.defaultValue as string,
    dob: dobFromState = dobConfig?.defaultValue as string,
    birthCountry: birthCountryFromState = birthCountryConfig?.defaultValue as string,
    nationality: nationalityFromState = nationalityConfig?.defaultValue as string,
    appliedForEUSS: appliedForEUSSFromState = undefined,
    grantedEUSSStatus: grantedEUSSStatusFromState = undefined,
    applyingFromHomeCountry = undefined,
    countryOfVisaApplication = countryOfVisaApplicationConfig?.defaultValue as string,
    livedInUKLast12Months: livedInUKLast12MonthsFromState = undefined,
    otherCountryReason = reasonWhyNotCountryOfNationalityConfig?.defaultValue as string,
    hasOtherNationality = hasOtherNationalityConfig?.defaultValue as boolean,
    otherNationalities: otherNationalitiesFromState = []
  } = useSelector((state: ApplicationState) => state.student.personalInfo);
  const isPageComplete = useSelector((state: ApplicationState) => state.student.offer.isPIPage2Complete);
  const otherGenderEnabled = useSelector((state: ApplicationState) => state.config.otherGenderEnabled);
  const config = useSelector((state: ApplicationState) => state.config);
  const importData = useSelector((state: ApplicationState) => state.student.importData);
  const sectionTitle = useSelector((state: ApplicationState) => state.config.Checklist.PersonalInfo.shortTitle);

  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = (config: any) => {

    dispatch(StudentActions.savePersonalInfo(Pages.PAGE2, config));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value: any) => {
    dispatch(PIActions.createUpdatePIFieldAction(fieldName, value));
  };
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const getImportApprovedFields = (): Array<Q> => {
    const importApproved = [];
    if (isPageComplete) {
      if (importData?.nationality === nationality) {
        importApproved.push(Q.NATIONALITY);
      }
      if (importData?.gender === gender) {
        importApproved.push(Q.GENDER);
      }
      if (importData?.dob && importData?.dob === dob) {
        importApproved.push(Q.DOB);
      }
      if (importData?.countryOfBirth === birthCountry) {
        importApproved.push(Q.BIRTH_COUNTRY);
      }
    }
    return importApproved;
  };

  const [date, setDate] = useState(getDateFromString(dobFromState));
  const [isDateValid, setIsDateValid] = useState(true);
  const [dateErrorMsg, setDateErrorMsg] = useState('');
  const [nationality, setNationality] = useState(nationalityFromState ? nationalityFromState : '');
  const [gender, setGender] = useState(genderFromState ? genderFromState : '');
  const [dob, setDob] = useState(dobFromState ? dobFromState : '');
  const [birthCountry, setBirthCountry] = useState(birthCountryFromState ? birthCountryFromState : '');
  const [importApproved, setImportApproved] = useState(getImportApprovedFields());
  const [importUpdateEnabled, setImportUpdateEnabled] = useState([]);
  const [importEdited, setImportEdited] = useState([]);
  const [nationalitySuggestions, setNationalitySuggestions] = useState(nationalitiesNames);
  const [appliedForEUSS, setAppliedForEUSS] = useState(appliedForEUSSFromState);
  const [grantedEUSSStatus, setGrantedEUSSStatus] = useState(grantedEUSSStatusFromState);
  const [countrySuggestions, setCountrySuggestions] = useState(countryNames);
  const [countryOfVisaApplicationValue, setCountryOfVisaApplicationValue] = useState(
    countryOfVisaApplication ? countryOfVisaApplication : ''
  );
  const [livedInUKLast12Months, setLivedInUKLast12Months] = useState(livedInUKLast12MonthsFromState ?? undefined);
  const [genderErrorMsg, setGenderErrorMsg] = useState('');
  const [otherNationalities, setOtherNationalities] = useState(otherNationalitiesFromState);
  const [showModal, setShowModal] = useState([...otherNationalitiesFromState.map(_ => false), false]);
  const [complete, setComplete] = useState(false);
  const [visible, setVisible] = useState(Visible);
  const [genderVisible, setGenderVisible] = useState(false);
  const [dobVisible, setDobVisible] = useState(false);
  const [birthCountryVisible, setBirthCountryVisible] = useState(false);
  const [nationalityVisible, setNationalityVisible] = useState(false);
  const [appliedForEUSSVisible, setAppliedForEUSSVisible] = useState(false);
  const [grantedEUSSStatusVisible, setGrantedEUSSStatusVisible] = useState(false);
  const [isCountryOfNationalityVisible, setIsCountryOfNationalityVisible] = useState(false);
  const [countryOfVisaApplicationVisible, setCountryOfVisaApplicationVisible] = useState(false);
  const [livedInUKLast12MonthsVisible, setLivedInUKLast12MonthsVisible] = useState(false);
  const [hasOtherNationalityVisible, setHasOtherNationalityVisible] = useState(false);

  useEffect(() => {
    const { complete: isComplete, visible: isVisible } = getPageStatus(config, {
      gender,
      dob,
      birthCountry,
      nationality,
      appliedForEUSS,
      grantedEUSSStatus,
      otherCountryReason,
      applyingFromHomeCountry,
      countryOfVisaApplication,
      livedInUKLast12Months,
      hasOtherNationality,
      otherNationalities
    });
    setComplete(dateErrorMsg === '' && isComplete);
    setVisible(isVisible);
  }, [
    config,
    gender,
    dob,
    birthCountry,
    appliedForEUSS,
    grantedEUSSStatus,
    otherCountryReason,
    applyingFromHomeCountry,
    countryOfVisaApplication,
    livedInUKLast12Months,
    hasOtherNationality,
    otherNationalities,
    dateErrorMsg,
    nationality
  ]);

  useEffect(() => {
    const {
      gender: genderVisible,
      dob: dobVisible,
      birthCountry: birthCountryVisible,
      nationality: nationalityVisible,
      appliedForEUSS: appliedForEUSSVisible,
      grantedEUSSStatus: grantedEUSSStatusVisible,
      isCountryOfNationality: isCountryOfNationalityVisible,
      countryOfVisaApplication: countryOfVisaApplicationVisible,
      livedInUKLast12Months: livedInUKLast12MonthsVisible,
      hasOtherNationality: hasOtherNationalityVisible
    } = visible;

    setGenderVisible(genderVisible);
    setDobVisible(dobVisible);
    setBirthCountryVisible(birthCountryVisible);
    setNationalityVisible(nationalityVisible);
    setAppliedForEUSSVisible(appliedForEUSSVisible);
    setGrantedEUSSStatusVisible(grantedEUSSStatusVisible);
    setIsCountryOfNationalityVisible(isCountryOfNationalityVisible);
    setCountryOfVisaApplicationVisible(countryOfVisaApplicationVisible);
    setLivedInUKLast12MonthsVisible(livedInUKLast12MonthsVisible);
    setHasOtherNationalityVisible(hasOtherNationalityVisible);
  }, [visible]);

  useEffect(() => {
    const importUpdate = [];
    if (isPageComplete) {
      if (importData.nationality && nationality !== importData.nationality) {
        importUpdate.push(Q.NATIONALITY);
      }
      if (importData.gender && gender !== importData.gender) {
        importUpdate.push(Q.GENDER);
      }
      if (importData.dob && dob !== importData.dob) {
        importUpdate.push(Q.DOB);
      }
      if (importData.countryOfBirth && birthCountry !== importData.countryOfBirth) {
        importUpdate.push(Q.BIRTH_COUNTRY);
      }
    }
    setImportUpdateEnabled(importUpdate);
  }, [isPageComplete]);

  const checkIsOtherGenderValid = (label?: string) => {
    const isValid = (otherGenderEnabled && label === 'Other') || label !== 'Other';
    return isValid;
  };

  useEffect(() => {
    setOtherNationalities(otherNationalitiesFromState);
    setShowModal([...otherNationalitiesFromState.map(_ => false), false]);
  }, [otherNationalitiesFromState]);

  const toggleModal = (index: number) => {
    if (showModal[index] !== undefined) {
      const newShowModal = [...showModal];
      newShowModal[index] = !showModal[index];
      setShowModal([...newShowModal]);
    }
  };

  useEffect(() => {
    const { YYYY, DD, MM } = date;
    if (YYYY && DD && MM && YYYY.length === 4) {
      const isValid = isValidDate(date);
      if (!isValid) {
        setIsDateValid(false);
        setDateErrorMsg(`Doesn't look like a valid date`);
        onChange(Q.DOB, '');
      } else {
        const momentDate = getMomentDate(date);

        const age = moment().diff(momentDate, 'years');
        if (age > 120) {
          setIsDateValid(false);
          setDateErrorMsg('Sorry, you are above our maximum age for application');
        } else {
          if (!isDateValid) {
            setIsDateValid(true);
            setDateErrorMsg('');
          }
          setDob(getStringFromDate(date));
          onChange(Q.DOB, getStringFromDate(date));
        }
      }
    }
  }, [date]);

  const isImportEnabled = (target: Q): boolean => {
    switch (target) {
      case Q.GENDER:
        return (
          !!importData.gender &&
          (importData.gender === 'Male' || importData.gender === 'Female' || checkIsOtherGenderValid(importData.gender))
        );
      case Q.DOB:
        return !!importData.dob;
      case Q.BIRTH_COUNTRY:
        return !!importData.countryOfBirth;
      case Q.NATIONALITY:
        return !!importData.nationality;
      default:
        return false;
    }
  };

  const isImportUpdateEnabled = (target: Q): boolean => {
    if (!isPageComplete) return false;
    switch (target) {
      case Q.GENDER:
        return !!gender && importUpdateEnabled.includes(target);
      case Q.DOB:
        return !!dob && importUpdateEnabled.includes(target);
      case Q.BIRTH_COUNTRY:
        return !!birthCountry && importUpdateEnabled.includes(target);
      case Q.NATIONALITY:
        return !!nationality && importUpdateEnabled.includes(target);
      default:
        return false;
    }
  };

  const handleYesNoClick = (target: Q, question: any): void => {
    if (target === Q.IS_COUNTRY_OF_NATIONALITY) {
      onChange(Q.REASON_WHY_NOT_COUNTRY_OF_NATIONALITY, '');
      onChange(Q.COUNTRY_OF_VISA_APPLICATION, '');
      onChange(Q.LIVED_IN_UK_LAST_12_MONTHS, undefined);
      setCountryOfVisaApplicationValue('');
      setLivedInUKLast12Months(undefined);
    }
    onChange(target, question.char === 'Y' ? true : false);
  };

  const handleAppliedForEUSSClick = (question: any) => {
    const answer =
      question.char === '1'
        ? AppliedEUSSStatus.GRANTED
        : question.char === '2'
        ? AppliedEUSSStatus.APPLIED
        : AppliedEUSSStatus.NO;

    setAppliedForEUSS(answer);
    onChange(Q.APPLIED_FOR_EUSS, answer);
  };

  const handleGrantedEUSSStatusClick = (question: any) => {
    const answer = question.char === '1' ? EUSSStatus.SETTLED : EUSSStatus.PRE_SETTLED;
    setGrantedEUSSStatus(answer);
    onChange(Q.GRANTED_EUSS_STATUS, answer);
  };

  const deleteOtherNationalities = (index: number): void => {
    const newOtherNationalities = [...otherNationalities.slice(0, index), ...otherNationalities.slice(index + 1)];

    setOtherNationalities([...newOtherNationalities]);
    onChange(Q.OTHER_NATIONALITIES, [...newOtherNationalities]);
  };

  const isImportComplete = (target: Q): boolean => {
    if (!isImportEnabled(target) || isImportUpdateEnabled(target)) return true;
    return importApproved.includes(target) || importEdited.includes(target);
  };

  const getComplete = (): boolean => {
    const importQuestions = [Q.GENDER, Q.DOB, Q.BIRTH_COUNTRY, Q.NATIONALITY];
    const importComplete = importQuestions.every(q => isImportComplete(q));
    return complete && importComplete;
  };

  const handleImportApprove = (target: Q): void => {
    setImportApproved([...importApproved, target]);
    setImportEdited(importEdited.filter(q => q !== target));
  };

  const handleImportEdit = (target: Q): void => {
    setImportApproved(importApproved.filter(q => q !== target));
    setImportEdited([...importEdited, target]);
  };

  const handleImportUndo = (target: Q): void => {
    setImportApproved(importApproved.filter(q => q !== target));
    setImportEdited(importEdited.filter(q => q !== target));
  };

  const handleClick = (question: any) => {
    const isValid = checkIsOtherGenderValid(question.label);

    setGender(question.label);
    setGenderErrorMsg(isValid ? '' : OtherGenderErrorMsg);
    if (isValid) {
      onChange(Q.GENDER, question.label);
    }
  };

  const handleNationalitiesChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = nationalitiesNames.filter(s => s.toLowerCase().includes(value.toLowerCase()));

    setNationalitySuggestions(suggestions);
    setNationality(value);

    if (nationalitiesNames.includes(value)) {
      setAppliedForEUSS(undefined);
      onChange(Q.NATIONALITY, value);
    }
  };

  const handleBirthCountryChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = countryNames.filter(s => s.toLowerCase().includes(value.toLowerCase()));

    setCountrySuggestions(suggestions);
    setBirthCountry(value);

    if (countryNames.includes(value)) {
      onChange(Q.BIRTH_COUNTRY, value);
    }
  };

  const handleCountryOfVisaApplicationChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = countryNames.filter(s => s.toLowerCase().includes(value.toLowerCase()));

    setCountrySuggestions(suggestions);
    setCountryOfVisaApplicationValue(value);

    if (countryNames.includes(value)) {
      onChange(Q.COUNTRY_OF_VISA_APPLICATION, value);
      onChange(Q.LIVED_IN_UK_LAST_12_MONTHS, undefined);

      setLivedInUKLast12Months(undefined);
    }
  };

  return (
    <QuestionPage
      complete={getComplete() && genderErrorMsg === ''}
      currentStep={currentStep}
      steps={steps}
      onNext={() => onNext(config)}
      onSkip={onSkip}
      onPrevious={onPrevious}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      {genderVisible && (
        <RadioOptionsWithConfirm
          key={Q.GENDER}
          id={Q.GENDER}
          value={gender}
          label={genderConfig?.label}
          onChange={(evt: any) => onChange(Q.GENDER, evt.currentTarget.value)}
          inputHelperText={genderConfig?.helpText}
          errorText={genderErrorMsg}
          onClick={(question: any) => handleClick(question)}
          questions={[
            {
              char: 'F',
              label: 'Female',
              selected: gender === 'Female'
            },
            {
              char: 'M',
              label: 'Male',
              selected: gender === 'Male'
            },
            {
              char: 'O',
              label: 'Other',
              selected: gender === 'Other'
            }
          ]}
          importEnabled={isImportEnabled(Q.GENDER)}
          importValue={importData.gender}
          importUpdateEnabled={isImportUpdateEnabled(Q.GENDER)}
          importApproved={importApproved.includes(Q.GENDER)}
          importEdited={importEdited.includes(Q.GENDER)}
          onImportApprove={() => {
            handleImportApprove(Q.GENDER);
            setGender(importData.gender);
            onChange(Q.GENDER, importData.gender);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.GENDER);
            onChange(Q.GENDER, isImportUpdateEnabled(Q.GENDER) ? gender : importData.gender);
          }}
          onImportUndo={() => {
            handleImportUndo(Q.GENDER);
            setGender(isImportUpdateEnabled(Q.GENDER) ? gender : '');
            onChange(Q.GENDER, isImportUpdateEnabled(Q.GENDER) ? gender : '');
          }}
        />
      )}

      {dobVisible && (
        <DateInputWithConfirm
          key={Q.DOB}
          id={Q.DOB}
          label={dobConfig?.label}
          value={date}
          inputHelperText={dobConfig?.helpText}
          errorText={dateErrorMsg}
          onChangeDD={(evt: any) => setDate({ ...date, DD: evt.currentTarget.value })}
          onChangeMM={(evt: any) => setDate({ ...date, MM: evt.currentTarget.value })}
          onChangeYY={(evt: any) => {
            if (evt.currentTarget.value.length === 4) {
              setDate({
                ...date,
                YYYY: evt.currentTarget.value
              });
            } else {
              setDate({
                ...date,
                YYYY: evt.currentTarget.value
              });
            }
          }}
          // onBlur={() => validateDate()}
          importEnabled={isImportEnabled(Q.DOB)}
          importValue={importData.dob}
          importUpdateEnabled={isImportUpdateEnabled(Q.DOB)}
          importApproved={importApproved.includes(Q.DOB)}
          importEdited={importEdited.includes(Q.DOB)}
          onImportApprove={() => {
            handleImportApprove(Q.DOB);
            setDate(getDateFromString(importData.dob));
            setDob(importData.dob);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.DOB);
            if (isImportUpdateEnabled(Q.DOB)) {
              setDate(date);
            } else {
              setDate(getDateFromString(importData.dob));
            }
          }}
          onImportUndo={() => {
            handleImportUndo(Q.DOB);
            if (isImportUpdateEnabled(Q.DOB)) {
              setDate(getDateFromString(dob));
            } else {
              setDate({
                DD: null,
                MM: null,
                YYYY: null
              });
            }
          }}
        />
      )}

      {birthCountryVisible && (
        <AutocompleteInputWithConfirm
          key={Q.BIRTH_COUNTRY}
          id={Q.BIRTH_COUNTRY}
          value={birthCountry}
          label={birthCountryConfig?.label}
          inputHelperText={birthCountryConfig?.helpText}
          onChange={(evt: any) => handleBirthCountryChange(evt)}
          suggestions={countrySuggestions}
          onBlur={() => {
            // setBirthCountryValue(birthCountryValue);
            setCountrySuggestions(countryNames);
          }}
          importEnabled={isImportEnabled(Q.BIRTH_COUNTRY)}
          importValue={importData.countryOfBirth}
          importUpdateEnabled={isImportUpdateEnabled(Q.BIRTH_COUNTRY)}
          importApproved={importApproved.includes(Q.BIRTH_COUNTRY)}
          importEdited={importEdited.includes(Q.BIRTH_COUNTRY)}
          onImportApprove={() => {
            handleImportApprove(Q.BIRTH_COUNTRY);
            setBirthCountry(importData.countryOfBirth);
            onChange(Q.BIRTH_COUNTRY, importData.countryOfBirth);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.BIRTH_COUNTRY);
            onChange(
              Q.BIRTH_COUNTRY,
              isImportUpdateEnabled(Q.BIRTH_COUNTRY) ? birthCountry : importData.countryOfBirth
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.BIRTH_COUNTRY);
            setBirthCountry(isImportUpdateEnabled(Q.BIRTH_COUNTRY) ? birthCountry : '');
            onChange(Q.BIRTH_COUNTRY, isImportUpdateEnabled(Q.BIRTH_COUNTRY) ? birthCountry : '');
          }}
        />
      )}

      {nationalityVisible && (
        <AutocompleteInputWithConfirm
          key={Q.NATIONALITY}
          id={Q.NATIONALITY}
          value={nationality}
          label={nationalityConfig?.label}
          inputHelperText={nationalityConfig?.helpText}
          onChange={(evt: any) => handleNationalitiesChange(evt)}
          suggestions={nationalitySuggestions}
          onBlur={() => {
            // setNationalityValue(nationalityValue);
            setNationalitySuggestions(nationalitiesNames);
          }}
          importEnabled={isImportEnabled(Q.NATIONALITY)}
          importValue={importData.nationality}
          importUpdateEnabled={isImportUpdateEnabled(Q.NATIONALITY)}
          importApproved={importApproved.includes(Q.NATIONALITY)}
          importEdited={importEdited.includes(Q.NATIONALITY)}
          onImportApprove={() => {
            handleImportApprove(Q.NATIONALITY);
            setNationality(importData.nationality);
            onChange(Q.NATIONALITY, importData.nationality);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.NATIONALITY);
            onChange(Q.NATIONALITY, isImportUpdateEnabled(Q.NATIONALITY) ? nationality : importData.nationality);
          }}
          onImportUndo={() => {
            handleImportUndo(Q.NATIONALITY);
            setNationality(isImportUpdateEnabled(Q.NATIONALITY) ? nationality : '');
            onChange(Q.NATIONALITY, isImportUpdateEnabled(Q.NATIONALITY) ? nationality : '');
          }}
        />
      )}

      {appliedForEUSSVisible && (
        <RadioOptionsWithConfirm
          key={Q.APPLIED_FOR_EUSS}
          id={Q.APPLIED_FOR_EUSS}
          value={appliedForEUSS}
          label={appliedForEUSSConfig?.label}
          inputHelperText={appliedForEUSSConfig?.helpText}
          onClick={(question: any) => handleAppliedForEUSSClick(question)}
          questions={[
            {
              char: '1',
              label: 'Yes',
              selected: appliedForEUSS === AppliedEUSSStatus.GRANTED
            },
            {
              char: '2',
              label: 'No, but I have applied for it',
              selected: appliedForEUSS === AppliedEUSSStatus.APPLIED
            },
            {
              char: '3',
              label: 'No',
              selected: appliedForEUSS === AppliedEUSSStatus.NO
            }
          ]}
        />
      )}

      {grantedEUSSStatusVisible && (
        <RadioOptionsWithConfirm
          key={Q.GRANTED_EUSS_STATUS}
          id={Q.GRANTED_EUSS_STATUS}
          value={grantedEUSSStatus}
          label={grantedEUSSStatusConfig?.label}
          inputHelperText={grantedEUSSStatusConfig?.helpText}
          onClick={(question: any) => handleGrantedEUSSStatusClick(question)}
          isInline={true}
          questions={[
            {
              char: '1',
              label: 'Settled',
              selected: grantedEUSSStatus === EUSSStatus.SETTLED
            },
            {
              char: '2',
              label: 'Pre-settled',
              selected: grantedEUSSStatus === EUSSStatus.PRE_SETTLED
            }
          ]}
        />
      )}

      {isCountryOfNationalityVisible && (
        <RadioOptionsWithConfirm
          key={Q.IS_COUNTRY_OF_NATIONALITY}
          id={Q.IS_COUNTRY_OF_NATIONALITY}
          value={applyingFromHomeCountry}
          label={isCountryOfNationalityConfig?.label}
          inputHelperText={isCountryOfNationalityConfig?.helpText}
          onClick={(question: any) => handleYesNoClick(Q.IS_COUNTRY_OF_NATIONALITY, question)}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: applyingFromHomeCountry === true
            },
            {
              char: 'N',
              label: 'No',
              selected: applyingFromHomeCountry === false
            }
          ]}
        />
      )}

      {countryOfVisaApplicationVisible && (
        <AutocompleteInputWithConfirm
          key={Q.COUNTRY_OF_VISA_APPLICATION}
          id={Q.COUNTRY_OF_VISA_APPLICATION}
          value={countryOfVisaApplicationValue}
          label={countryOfVisaApplicationConfig?.label}
          onChange={(evt: any) => handleCountryOfVisaApplicationChange(evt)}
          suggestions={countrySuggestions}
          onBlur={() => {
            // setCountryOfVisaApplicationValue(countryOfVisaApplication);
            setCountrySuggestions(countryNames);
          }}
        />
      )}

      {livedInUKLast12MonthsVisible && (
        <RadioOptionsWithConfirm
          key={Q.LIVED_IN_UK_LAST_12_MONTHS}
          id={Q.LIVED_IN_UK_LAST_12_MONTHS}
          value={livedInUKLast12Months}
          label={livedInUKLast12MonthsConfig?.label}
          subText={livedInUKLast12MonthsConfig?.subLabel}
          inputHelperText={livedInUKLast12MonthsConfig?.helpText}
          onClick={(question: any) => {
            setLivedInUKLast12Months(question.char === 'Y');
            handleYesNoClick(Q.LIVED_IN_UK_LAST_12_MONTHS, question);
          }}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: livedInUKLast12Months === true
            },
            {
              char: 'N',
              label: 'No',
              selected: livedInUKLast12Months === false
            }
          ]}
        />
      )}

      {applyingFromHomeCountry === false && (
        <TextareaWithConfirm
          id={Q.REASON_WHY_NOT_COUNTRY_OF_NATIONALITY}
          value={otherCountryReason}
          label={reasonWhyNotCountryOfNationalityConfig?.label}
          inputHelperText={reasonWhyNotCountryOfNationalityConfig?.helpText}
          onChange={(evt: React.FormEvent<HTMLInputElement>) => {
            onChange(Q.REASON_WHY_NOT_COUNTRY_OF_NATIONALITY, evt.currentTarget.value);
          }}
        />
      )}

      {hasOtherNationalityVisible && (
        <RadioOptionsWithConfirm
          key={Q.HAS_OTHER_NATIONALITY}
          id={Q.HAS_OTHER_NATIONALITY}
          value={hasOtherNationality}
          label={hasOtherNationalityConfig?.label}
          inputHelperText={hasOtherNationalityConfig?.helpText}
          onClick={(question: any) => {
            onChange(Q.HAS_OTHER_NATIONALITY, question.label === 'Yes');
          }}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasOtherNationality === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasOtherNationality === false
            }
          ]}
        />
      )}

      {hasOtherNationality === true && (
        <CardContainer title={'Other Nationalities'}>
          {[...otherNationalities, { nationality: '', isCurrent: undefined }].map(
            (nationality: IOtherNationality, index: number) => (
              <CardWithModal
                key={index}
                id={`other-nationality-${index}`}
                index={index}
                modalTitle={'Add An Alternative Or Previous Nationality'}
                cardItems={[
                  {
                    label: 'Nationality',
                    value: nationality.nationality
                  },
                  {
                    label: 'Current/Former',
                    value: `${nationality.isCurrent ? 'Current' : 'Former'} Nationality`
                  }
                ]}
                onRemove={deleteOtherNationalities}
                addButtonLabel={'Add a nationality'}
                addAnotherButtonLabel={'I have another nationality to add'}
                hasAddMoreButton={nationality.nationality === '' && nationality.isCurrent === undefined}
                showModal={showModal[index]}
                toggleModal={toggleModal}
              >
                <OtherNationality
                  index={index}
                  nationality={nationality.nationality}
                  isCurrent={nationality.isCurrent}
                  suggestions={nationalitySuggestions}
                  onSaveAndClose={(otherNationality: any) => {
                    const newOtherNationalities = [...otherNationalities];
                    newOtherNationalities[index] = {
                      ...otherNationality
                    };
                    setOtherNationalities(newOtherNationalities);
                    onChange(Q.OTHER_NATIONALITIES, newOtherNationalities);
                    toggleModal(index);
                  }}
                />
              </CardWithModal>
            )
          )}
        </CardContainer>
      )}
    </QuestionPage>
  );
};

export default PersonalQPage2;
